import * as React from 'react';

import type { FontAwesomeIconProps } from '@fortawesome/react-fontawesome';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Badge } from '@rtkwlf/fenrir-react';
import clsx from 'clsx';

import { focusStateToAppearance } from '../../../constants/focus-state-to-appearance';
import { useSecurityJourneyTranslations } from '../../../locales/use-security-journey-translations';

import styles from './focus-status-badge.module.css';

/* -------------------------------------------------------------------------------------------------
 * FocusStatusBadge
 * -----------------------------------------------------------------------------------------------*/
type FocusStatusBadgeElementType = React.ElementRef<typeof Badge.Root>;
type FocusStatusBadgeComponentProps = React.ComponentPropsWithoutRef<
  typeof Badge.Root
>;

export interface FocusStatusBadgeProps extends FocusStatusBadgeComponentProps {
  state: keyof typeof focusStateToAppearance;
}

export const FocusStatusBadge = React.forwardRef<
  FocusStatusBadgeElementType,
  FocusStatusBadgeProps
>(({ className, children, state, cursor, ...rest }, forwardedRef) => {
  /**
   * Init translations
   */
  const { t } = useSecurityJourneyTranslations();

  return (
    <Badge.Root
      className={clsx(className, styles['focus-status-badge'])}
      ref={forwardedRef}
      appearance={
        state in focusStateToAppearance
          ? focusStateToAppearance[state].appearance
          : 'default'
      }
      cursor={cursor}
      {...rest}
    >
      {state in focusStateToAppearance && focusStateToAppearance[state].icon ? (
        <Badge.Icon marginRight="xsmall">
          <FontAwesomeIcon
            icon={
              focusStateToAppearance[state].icon as FontAwesomeIconProps['icon']
            }
          />
        </Badge.Icon>
      ) : null}
      <Badge.Text>
        {state in focusStateToAppearance
          ? t(focusStateToAppearance[state].text)
          : t('securityFocuses.focusStatus.unknown')}
      </Badge.Text>
      {children}
    </Badge.Root>
  );
});

FocusStatusBadge.displayName = 'FocusStatusBadge';
